import { defaultCardTheme } from "./cardTheme";

export const trackedCardFields = [
  "firstName",
  "lastName",
  "role",
  "companyName",
  "description",
  "mobilePrefix",
  "mobile",
  "phonePrefix",
  "phone",
  "email",
  "companyEmail",
  "website",
  "address",
  "city",
  "zipCode",
  "avatarUrl",
  "logoUrl",
  "linkedin",
  "facebook",
  "instagram",
  "twitter",
  "youtube",
  "tiktok",
  "videoUrl",
  "videoTitle",
  "billingCompanyName",
  "vat",
  "pec",
  "electronicInvoicingId",
  "billingAddress",
  "billingCity",
  "billingZipCode",
  "themePreset",
  "themePrimary",
  "themeSecondary",
  "themeAccent",
  "themeBackground",
  "themeSurface",
  "themeText",
  "themeFont",
  "themeRadius",
];

const hasText = (value) => Boolean(String(value || "").trim());

const normalizeValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  return String(value).trim();
};

const hasCustomTheme = (card = {}) =>
  hasText(card.themePreset) && card.themePreset !== defaultCardTheme.preset
    ? true
    : [
        ["themePrimary", defaultCardTheme.primary],
        ["themeSecondary", defaultCardTheme.secondary],
        ["themeAccent", defaultCardTheme.accent],
        ["themeBackground", defaultCardTheme.background],
        ["themeSurface", defaultCardTheme.surface],
        ["themeText", defaultCardTheme.text],
        ["themeFont", defaultCardTheme.font],
        ["themeRadius", defaultCardTheme.radius],
      ].some(([fieldName, defaultValue]) =>
        hasText(card[fieldName]) && card[fieldName] !== defaultValue,
      );

export const getCardFeatureSnapshot = (card = {}) => {
  const plan = card.plan || "";
  const hasPlusPlan = plan === "plus";
  const documents = Array.isArray(card.documents) ? card.documents : [];
  const featureFlags = {
    avatar: hasText(card.avatarUrl),
    logo: hasText(card.logoUrl),
    company_contacts: hasText(card.companyEmail) || hasText(card.phone),
    website: hasText(card.website),
    map: hasText(card.address) || hasText(card.city),
    billing_data:
      hasText(card.billingCompanyName) ||
      hasText(card.vat) ||
      hasText(card.pec) ||
      hasText(card.electronicInvoicingId) ||
      hasText(card.billingAddress) ||
      hasText(card.billingCity) ||
      hasText(card.billingZipCode),
    social_linkedin: hasText(card.linkedin),
    social_facebook: hasText(card.facebook),
    social_instagram: hasText(card.instagram),
    social_twitter: hasText(card.twitter),
    social_youtube: hasText(card.youtube),
    social_tiktok: hasText(card.tiktok),
    video: hasText(card.videoUrl),
    documents: documents.length > 0,
    theme_custom: hasCustomTheme(card),
    qr_public: hasPlusPlan,
    qr_download: hasText(card.slug) || hasText(card.cardUrl) || hasText(card.url),
    email_signature: hasPlusPlan,
    avatar_ai: hasPlusPlan,
  };
  const featuresEnabled = Object.entries(featureFlags)
    .filter(([, enabled]) => enabled)
    .map(([featureName]) => featureName)
    .sort();

  return {
    featureFlags,
    featuresEnabled,
    featuresEnabledCount: featuresEnabled.length,
  };
};

export const getChangedFields = (previousCard = {}, nextCard = {}) =>
  trackedCardFields.filter(
    (fieldName) =>
      normalizeValue(previousCard[fieldName]) !==
      normalizeValue(nextCard[fieldName]),
  );

export const getChangedFeatures = (previousCard = {}, nextCard = {}) => {
  const previousFeatures = new Set(
    getCardFeatureSnapshot(previousCard).featuresEnabled,
  );
  const nextFeatures = new Set(getCardFeatureSnapshot(nextCard).featuresEnabled);
  const enabled = [...nextFeatures].filter(
    (featureName) => !previousFeatures.has(featureName),
  );
  const disabled = [...previousFeatures].filter(
    (featureName) => !nextFeatures.has(featureName),
  );
  const changed = [...new Set([...enabled, ...disabled])].sort();

  return {
    enabled: enabled.sort(),
    disabled: disabled.sort(),
    changed,
  };
};
